import { useEffect, useState } from 'react';

// SECURITY (TCX-797): decodeToken/getStoredRole read the JWT role by
// base64-decoding the payload WITHOUT verifying the signature. This is a UX
// gate ONLY — it decides which screens to render/redirect, not what the user
// is allowed to do. A client can forge any role here. Every role-gated action
// MUST be enforced server-side on the endpoint itself (see SEC-06/SEC-10);
// never trust this value as an authorization boundary.
const decodeToken = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (error) {
    console.error('Failed to decode token', error);
    return null;
  }
};

// TCX-666: synchronous role read for first-render gating decisions (route
// guards). The hook below sets role from an effect, so it is null on the first
// render — which would let a protected outlet mount (and fire its API calls)
// before a redirect runs.
export const getStoredRole = (): string | null => {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  return decodeToken(token)?.role ?? null;
};

// TCX-790: token-derived user id for scoping React Query keys. Identity MUST
// come from the access token (payload `sub`), never Redux or the profile
// mirror — those are stale during a same-tab account swap (TCX-699/TCX-632).
// Returns null when signed out so callers can fall back to unscoped/public
// behaviour. Reuses the same `decodeToken` as the role read above.
export const getUserIdFromToken = (): string | null => {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  return decodeToken(token)?.sub ?? null;
};

export const useAuthValidation = () => {
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    setRole(getStoredRole());
  }, []);

  return role;
};
